/**
 * Consolidatie detailpagina — info-kaarten + actiebalk + tabs.
 *
 * 4 info-kaarten bovenaan:
 *   1. Consolidatie  (#, status, route, type/meting, datum, aangemaakt-door, laatste update)
 *   2. Vertrekcarrier (label, country, agency)
 *   3. Bestemmingscarrier
 *   4. Aanvullende kenmerken (type, agency, master_awb, services)
 *
 * Actiebalk:
 *   - Nieuwe consolidatie (terug + nieuwe)
 *   - Zendingen toevoegen (scroll naar chip-input)
 *   - Label (stub)
 *   - Rapportages (stub-menu)
 *   - Nieuw pakket (stub)
 *
 * Tabs:
 *   - Zendingen (volledige tabel met chip-input bovenaan + totalen onderaan)
 *   - Pakketten skeleton (telling + "Komt binnenkort")
 *   - Documenten skeleton (telling + "Komt binnenkort")
 *   - Activiteit (audit-log filtered op deze consolidatie)
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Layers, ArrowLeft, Plus, FileText, BarChart3, Package, ChevronDown,
  AlertTriangle, Loader2, X, Trash2, ArrowRight, Building2, MapPin, Tag,
  Boxes, Activity, CheckCircle2, Download, FileSpreadsheet, FileCode,
  FileImage, Ship, Plane, Search,
} from "lucide-react";

import { adminApi } from "../../../context/AuthContext";
import { useAuth } from "../../../context/AuthContext";
import { useGlobalLoading } from "../../../context/GlobalLoadingProvider";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import ServiceTypeBadge from "./ServiceTypeBadge";
import DestinationBadge from "./DestinationBadge";
import { ScanButton } from "./ScanButton";
import ShipmentLookupDialog from "./ShipmentLookupDialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../ui/dropdown-menu";

import { StatusPill } from "./ConsolidationsSection";
import SelectShipmentsForConsolidationDialog from "./SelectShipmentsForConsolidationDialog";
import ConsolidationCockpitPanel from "./ConsolidationCockpitPanel";
import ConsolidationPackagesTab from "./ConsolidationPackagesTab";
import ConsolidationDocumentsTab from "./ConsolidationDocumentsTab";
import ConsolidationLabelButton from "./ConsolidationLabelButton";
import { ManifestV5Panel } from "../../ManifestV5Panel";
import { ConsolidationShipmentChoice } from "../operational/ConsolidationShipmentChoice";
import { ConsolidationSyncNotice, consolidationStatusLabels, MilestoneExecutionNotice } from "../operational/ConsolidationSyncNotice";

const MEASUREMENT_LABELS = {
  aero_lb: "Aero (lb)",
  aero_kl: "Aero (kg)",
  maritimo_cf: "Maritiem (cu.ft)",
  maritimo_cbm: "Maritiem (CBM)",
  by_ground: "Over land",
};
const TYPE_LABELS = {
  courier: "Courier",
  warehouse: "Warehouse",
  awr_awb: "AWR / AWB",
};

function fmtNum(n, digits = 2) {
  return Number(n || 0).toFixed(digits).replace(".", ",");
}

export default function ConsolidationDetail({ consolidationId, onBack }) {
  const { hasPermission } = useAuth();
  const [syncReport, setSyncReport] = useState(null);
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("zendingen");
  const [shipmentMatch, setShipmentMatch] = useState(null), [forceSelections, setForceSelections] = useState({});
  const [addBusy, setAddBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [forceQueue, setForceQueue] = useState([]);
  const [removeDialog, setRemoveDialog] = useState(null);   // {link_id, w_number}
  const [statusDialog, setStatusDialog] = useState(false);
  const [selectShipmentsOpen, setSelectShipmentsOpen] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const workflow = meta?.milestones_catalog || [];
  const statusLabels = consolidationStatusLabels(workflow);
  const displayTimezone = data?.automation?.timezone || "Europe/Amsterdam";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, d] = await Promise.all([
        meta ? Promise.resolve({ data: meta }) : adminApi.get("/admin/consolidations/meta"),
        adminApi.get(`/admin/consolidations/${consolidationId}`),
      ]);
      if (!meta) setMeta(m.data);
      setData(d.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [consolidationId, meta]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let active = true;
    const interval = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      try {
        const response = await adminApi.get(`/admin/consolidations/${consolidationId}`);
        if (active) { setData(response.data); setSyncReport(null); }
      } catch { /* Existing session handling remains authoritative. */ }
    }, 15000);
    return () => { active = false; clearInterval(interval); };
  }, [consolidationId]);

  const canEdit = !!meta?.permissions?.can_edit;
  const canAddShipment = !!meta?.permissions?.can_add_shipment;
  const isLocked = data && (data.status === "gesloten" || data.status === "geannuleerd");

  // Fase 2 (Feb 2026) — inline edit voor Container/ETD/ETA. Patched direct
  // via de bestaande PATCH endpoint; local state wordt merged na succes zodat
  // de manifest-export knop meteen de nieuwste waardes gebruikt.
  const handleHeaderFieldSave = async (field, value) => {
    try {
      const payload = { [field]: value === "" ? null : value };
      const r = await adminApi.patch(
        `/admin/consolidations/${consolidationId}`, payload,
      );
      setData((prev) => (prev ? { ...prev, ...r.data } : prev));
      toast.success("Bijgewerkt");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bijwerken mislukt");
      return false;
    }
  };

  const handleAddShipments = async (wNumbers, force = false, choices = {}) => {
    if (!wNumbers || wNumbers.length === 0) return;
    setAddBusy(true);
    try {
      const r = await adminApi.post(`/admin/consolidations/${consolidationId}/shipments`, {
        w_numbers: wNumbers, force, shipment_choices: choices,
      });
      const { added, errors, warnings: ws } = r.data;
      setShipmentMatch(null);
      setData((prev) => ({ ...prev, shipments: r.data.shipments, totals: r.data.totals }));
      if (added.length > 0) toast.success(`${added.length} zending(en) toegevoegd`);
      errors.forEach((e) => toast.error(e.message));
      if (ws && ws.length > 0) {
        setWarnings(ws);
        setForceQueue(ws.map((w) => w.w_number));
        setForceSelections(choices);
      } else {
        setWarnings([]);
        setForceQueue([]);
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.candidates?.length) setShipmentMatch({ detail, wNumbers, force, choices });
      else toast.error(typeof detail === "string" ? detail : "Toevoegen mislukt");
    } finally {
      setAddBusy(false);
    }
  };

  const handleForceConfirm = async () => {
    await handleAddShipments(forceQueue, true, forceSelections);
  };

  const handleRemove = async (linkId, reason) => {
    try {
      await adminApi({
        method: "DELETE",
        url: `/admin/consolidations/${consolidationId}/shipments/${linkId}`,
        data: { reason },
      });
      toast.success("Zending verwijderd uit consolidatie");
      setRemoveDialog(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verwijderen mislukt");
    }
  };

  if (loading) {
    return (
      <div className="bg-white p-10 text-center text-gray-500">
        <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> Laden…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="consolidation-detail">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#082c50] to-[#0a3a6b] text-white px-5 py-4" data-testid="consolidation-blue-header">
        <div className="flex items-center gap-2 text-xs text-blue-100 mb-2">
          <button onClick={onBack} className="flex items-center hover:text-white" data-testid="consolidation-back">
            <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Consolidaties
          </button>
          <span>/</span>
          <span className="font-mono">{data.consolidation_number}</span>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Layers className="w-7 h-7 text-yellow-400" />
            <div>
              <h1 className="font-chivo font-black text-2xl tracking-tight" data-testid="consolidation-title">
                Consolidatie {data.consolidation_number}
              </h1>
              <p className="text-xs text-blue-100 mt-0.5">
                {data.departure_hub_label} <ArrowRight className="inline w-3 h-3 mx-1" /> {data.destination_hub_label}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!data.operational_status && <StatusPill status={data.status} />}
            {data.operational_status && <span className="text-sm font-semibold break-words" data-testid="consolidation-operational-status">
              {statusLabels[data.operational_status] || data.operational_status}
            </span>}
            {hasPermission("consolidation:status_edit") && !isLocked && (
              <Button size="sm" onClick={() => setStatusDialog(true)}
                      className="bg-white/10 hover:bg-white/20 text-white rounded-none text-xs"
                      data-testid="consolidation-change-status">
                Status wijzigen <ChevronDown className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <InfoCard icon={<Layers className="w-4 h-4" />} title="Consolidatie" testid="info-consolidation">
          <Row k="Nummer" v={<span className="font-mono">{data.consolidation_number}</span>} />
          <Row k="Status" v={<span data-testid="consolidation-summary-status">
            {data.operational_status ? statusLabels[data.operational_status] || data.operational_status : <StatusPill status={data.status} />}
          </span>} />
          <Row k="Type" v={TYPE_LABELS[data.type] || data.type} />
          <Row k="Meting" v={MEASUREMENT_LABELS[data.measurement] || data.measurement} />
          <Row k="Aangemaakt" v={<span data-testid="consolidation-created-at">
            {formatZonedTimestamp(data.created_at, displayTimezone)}
          </span>} />
          <Row k="Door" v={data.created_by_email || "—"} />
          <Row k="Laatste update" v={<span data-testid="consolidation-updated-at">
            {formatZonedTimestamp(data.updated_at, displayTimezone)}
            <span className="block text-[10px] text-gray-500" data-testid="consolidation-display-timezone">{displayTimezone}</span>
          </span>} />
        </InfoCard>

        <InfoCard icon={<Building2 className="w-4 h-4" />} title="Vertrekcarrier" testid="info-departure">
          <Row k="Hub" v={<strong>{data.departure_hub_label}</strong>} />
          {data.country && <Row k="Land" v={data.country} />}
          {data.agency && <Row k="Agency" v={data.agency} />}
          {data.office && <Row k="Office" v={data.office} />}
        </InfoCard>

        <InfoCard icon={<MapPin className="w-4 h-4" />} title="Bestemmingscarrier" testid="info-destination">
          <Row k="Hub" v={<strong>{data.destination_hub_label}</strong>} />
          {data.country && <Row k="Land" v={data.country} />}
        </InfoCard>

        <InfoCard icon={<Tag className="w-4 h-4" />} title="Aanvullende kenmerken" testid="info-extras">
          <Row k="Type" v={TYPE_LABELS[data.type] || data.type} />
          <Row k="Agency" v={data.agency || "—"} />
          <Row k="Master AWB" v={data.master_awb ? <span className="font-mono text-xs">{data.master_awb}</span> : "—"} />
          <Row k="Services" v={data.services_enabled ? <span className="text-emerald-700 font-bold">Aan</span> : "Uit"} />
          {data.internal_note && (
            <div className="mt-2 pt-2 border-t border-gray-100">
              <div className="text-[9px] uppercase font-bold text-gray-500 mb-0.5">Notitie</div>
              <div className="text-xs text-gray-700 italic">{data.internal_note}</div>
            </div>
          )}
        </InfoCard>
      </div>

      {/* Operationeel dossier — inline-editable canonical consolidatievelden.
          Consolidation Editability Sprint (Jun 2026): alle velden hieronder
          vloeien 1-op-1 door naar manifest (consolidation_export_service),
          consolidation label (consolidation_label_pdf) en automation. Het
          consolidatienummer zelf is immutable. */}
      <CollapsibleSection
        icon={<Ship className="w-4 h-4" />}
        title="Transport & verzending"
        hint="Direct in manifest & label"
        testid="info-verzending"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <EditableField
            label="Carrier"
            value={data.carrier || ""}
            placeholder="Bijv. Maersk of KLM Cargo"
            testid="edit-carrier"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("carrier", v)}
          />
          <EditableField
            label="Vessel / Vlucht"
            value={data.vessel_or_flight || ""}
            placeholder="Bijv. MSC AMBITION / KL785"
            testid="edit-vessel-or-flight"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("vessel_or_flight", v)}
          />
          <EditableField
            label="Vessel naam"
            value={data.vessel_name || ""}
            placeholder="Naam schip"
            testid="edit-vessel-name"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("vessel_name", v)}
          />
          <EditableField
            label="Vluchtnummer"
            value={data.flight_number || ""}
            placeholder="Bijv. KL785"
            testid="edit-flight-number"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("flight_number", v)}
          />
          <EditableField
            label="Voyage / Reisnummer"
            value={data.voyage || ""}
            placeholder="Bijv. 2026-W12"
            testid="edit-voyage"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("voyage", v)}
          />
          <EditableField
            label="Containernummer"
            value={data.container_number || ""}
            placeholder="Bijv. MSKU-1234567"
            testid="edit-container"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("container_number", v)}
          />
          <EditableField
            label="Sealnummer"
            value={data.seal_number || ""}
            placeholder="Bijv. NL0091234"
            testid="edit-seal-number"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("seal_number", v)}
          />
          <EditableField
            label="Booking / referentie"
            value={data.booking_reference || ""}
            placeholder="Bijv. BKG-99871"
            testid="edit-booking-reference"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("booking_reference", v)}
          />
          <EditableField
            label="Master AWB"
            value={data.master_awb || ""}
            placeholder="Bijv. 074-12345675"
            testid="edit-master-awb"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("master_awb", v)}
          />
          <EditableField
            label="ETD (vertrek)"
            value={data.etd || ""}
            placeholder="YYYY-MM-DD"
            type="date"
            testid="edit-etd"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("etd", v)}
          />
          <EditableField
            label="ETA (aankomst)"
            value={data.eta || ""}
            placeholder="YYYY-MM-DD"
            type="date"
            testid="edit-eta"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("eta", v)}
          />
        </div>
      </CollapsibleSection>

      {/* Route, agentschap & administratie */}
      <CollapsibleSection
        icon={<MapPin className="w-4 h-4" />}
        title="Route, agentschap & administratie"
        hint="Consolidatienummer is niet wijzigbaar"
        testid="info-route-admin"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <EditableField
            label="Vertrekhub (origin)"
            value={data.departure_hub_label || ""}
            placeholder="Bijv. Rotterdam"
            testid="edit-departure-hub"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("departure_hub_label", v)}
          />
          <EditableField
            label="Bestemmingshub (destination)"
            value={data.destination_hub_label || ""}
            placeholder="Bijv. Willemstad"
            testid="edit-destination-hub"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("destination_hub_label", v)}
          />
          <EditableField
            label="Land"
            value={data.country || ""}
            placeholder="Bijv. Curaçao"
            testid="edit-country"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("country", v)}
          />
          <EditableField
            label="Office / vestiging"
            value={data.office || ""}
            placeholder="Bijv. Rotterdam Warehouse"
            testid="edit-office"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("office", v)}
          />
          <EditableField
            label="Agentschap / agency"
            value={data.agency || ""}
            placeholder="Bijv. Caribbean Agencies NV"
            testid="edit-agency"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("agency", v)}
          />
          <EditableSelectField
            label="Type"
            value={data.type || ""}
            options={Object.entries(TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
            testid="edit-type"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("type", v)}
          />
          <EditableSelectField
            label="Meting"
            value={data.measurement || ""}
            options={Object.entries(MEASUREMENT_LABELS).map(([k, v]) => ({ value: k, label: v }))}
            testid="edit-measurement"
            editable={canEdit && !isLocked}
            onSave={(v) => handleHeaderFieldSave("measurement", v)}
          />
          <div className="md:col-span-3">
            <EditableField
              label="Operationele notitie"
              value={data.internal_note || ""}
              placeholder="Interne opmerking bij deze consolidatie"
              testid="edit-internal-note"
              editable={canEdit && !isLocked}
              onSave={(v) => handleHeaderFieldSave("internal_note", v)}
            />
          </div>
        </div>
      </CollapsibleSection>

      {/* Action bar */}
      <div className="bg-white border border-gray-200 p-3 flex items-center gap-2 flex-wrap" data-testid="consolidation-action-bar">
        <Button size="sm" onClick={onBack} variant="outline" className="rounded-none text-xs"
                data-testid="consolidation-action-back-list">
          <Plus className="w-3 h-3 mr-1" /> Nieuwe consolidatie
        </Button>
        {canAddShipment && !isLocked && (
          <Button size="sm" onClick={() => setSelectShipmentsOpen(true)}
                  className="bg-[#0055FF] text-white rounded-none text-xs font-bold uppercase"
                  data-testid="consolidation-action-add-shipments">
            <Plus className="w-3 h-3 mr-1" /> Zendingen toevoegen
          </Button>
        )}
        <Button size="sm" onClick={() => setLookupOpen(true)}
                variant="outline"
                className="rounded-none text-xs font-bold uppercase border-[#082c50] text-[#082c50] hover:bg-[#082c50] hover:text-white"
                data-testid="consolidation-action-shipment-lookup">
          <Search className="w-3 h-3 mr-1" /> Zending opzoeken
        </Button>
        <ConsolidationLabelButton
          consolidationId={consolidationId}
          consolidationNumber={data?.consolidation_number}
        />
        <ManifestV5Panel client={adminApi} consolidationId={consolidationId} />
        <ExportMenu consolidationId={consolidationId} consolidationNumber={data?.consolidation_number} />
        <StubButton label="Nieuw pakket" icon={<Package className="w-3 h-3" />} testid="consolidation-action-new-package" />
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200">
        <div className={`flex border-b border-gray-200 ${tab === "cockpit" ? "flex-wrap" : "overflow-x-auto"}`}>
          {[
            { key: "zendingen",  label: `Zendingen (${data.totals?.count || 0})` },
            { key: "cockpit",    label: "Consolidation Automation" },
            { key: "pakketten",  label: `Pakketten (${data.totals?.count || 0})` },
            { key: "documenten", label: "Documenten" },
            { key: "activiteit", label: "Activiteit" },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`px-4 py-3 text-xs font-bold uppercase tracking-wide shrink-0 whitespace-nowrap ${tab === t.key ? "border-b-2 border-[#0055FF] text-[#082c50]" : "text-gray-500"}`}
                    data-testid={`consolidation-tab-${t.key}`}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-3 sm:p-4 min-w-0">
          {tab === "zendingen" && (
            <ShipmentsTab
              data={data}
              statusLabels={statusLabels}
              canAdd={canAddShipment && !isLocked}
              canEdit={canEdit && !isLocked}
              isLocked={isLocked}
              addBusy={addBusy}
              onAdd={handleAddShipments}
              onRemove={(row) => setRemoveDialog(row)}
              onShipmentPatched={(sid, patch) => {
                // Fase 3 (Feb 2026) — merge inline-edit resultaat direct in
                // de detail-state zodat de manifest-export knop meteen de
                // nieuwe waardes gebruikt (single source of truth).
                setData((prev) => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    shipments: (prev.shipments || []).map((s) =>
                      s.shipping_label_id === sid || s.id === sid || s.label_id === sid
                        ? { ...s, ...patch }
                        : s,
                    ),
                  };
                });
              }}
            />
          )}
          {tab === "planning" && (
            <ConsolidationCockpitPanel
              consolidationId={consolidationId}
              data={data}
              onReload={load}
          onDiagnostics={setData}
          onSyncReport={setSyncReport}
          diagnosticsTargetId={`consolidation-execution-bottom-${consolidationId}`}
            />
          )}
          {tab === "cockpit" && (
            <ConsolidationCockpitPanel
              consolidationId={consolidationId}
              data={data}
              onReload={load}
            />
          )}
          {tab === "pakketten" && <ConsolidationPackagesTab data={data} />}
          {tab === "documenten" && <ConsolidationDocumentsTab consolidationId={consolidationId} />}
          {tab === "activiteit" && <ActivityTab consolidationId={consolidationId} />}
        </div>
      </div>

      {/* Warning dialog when shipments are in other open consolidation */}
      {warnings.length > 0 && (
        <Dialog open={true} onOpenChange={() => { setWarnings([]); setForceQueue([]); }}>
          <DialogContent className="max-w-lg" data-testid="consolidation-force-dialog">
            <DialogHeader>
              <DialogTitle className="font-chivo font-black text-amber-700 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> W-nummer(s) zitten in andere consolidatie
              </DialogTitle>
              <DialogDescription>
                Deze W-nummers zitten nog in een andere open consolidatie. Weet je zeker dat je ze wilt overzetten?
              </DialogDescription>
            </DialogHeader>
            <ul className="text-sm space-y-1">
              {warnings.map((w) => (
                <li key={w.w_number} className="font-mono text-xs">
                  <strong>{w.w_number}</strong> — zit in <strong>{w.consolidation_number}</strong>
                </li>
              ))}
            </ul>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => { setWarnings([]); setForceQueue([]); }}
                      className="rounded-none text-xs">Annuleren</Button>
              <Button onClick={handleForceConfirm}
                      className="bg-amber-600 hover:bg-amber-700 text-white rounded-none text-xs"
                      data-testid="consolidation-force-confirm">
                Ja, overzetten
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Status change dialog */}
      <section className="border-t border-slate-300 pt-5 space-y-4 min-w-0" data-testid="consolidation-diagnostics">
        <h2 className="text-base md:text-lg font-semibold" data-testid="consolidation-diagnostics-title">Verwerking &amp; diagnostiek</h2>
        <ConsolidationSyncNotice report={syncReport || data.shipment_sync} />
        <MilestoneExecutionNotice consolidation={data} catalog={workflow} />
        <div id={`consolidation-execution-bottom-${consolidationId}`} data-testid="consolidation-execution-bottom" />
      </section>
      {statusDialog && (
        <StatusChangeDialog
          current={data.operational_status || ""}
          options={workflow.map(m => m.canonical_status)}
          labels={statusLabels}
          onClose={() => setStatusDialog(false)}
          onConfirm={async (newStatus, reason, requestId) => {
            try {
              if (newStatus.startsWith("admin:")) {
                await adminApi.post(`/admin/consolidations/${consolidationId}/status`, {
                  status: newStatus.slice(6), reason,
                });
                toast.success("Administratieve status opgeslagen; operationele status ongewijzigd.");
              } else {
                const { data: outcome } = await adminApi.post(`/admin/consolidations/${consolidationId}/status-v2`, {
                  new_status: newStatus, reason, request_id: requestId,
                });
                setSyncReport(outcome.shipment_sync);
                if (outcome.shipment_sync?.complete === false) toast.warning("Update onvolledig. Bekijk de zendingen en blokkaderedenen.", { position: "bottom-right" });
                else toast.success(`Status bijgewerkt naar '${statusLabels[newStatus] || newStatus}'`, { position: "bottom-right" });
              }
              setStatusDialog(false);
              await load();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Statuswijziging mislukt", { position: "bottom-right" });
            }
          }}
        />
      )}

      {/* Remove shipment dialog */}
      {removeDialog && (
        <RemoveShipmentDialog
          shipment={removeDialog}
          onClose={() => setRemoveDialog(null)}
          onConfirm={(reason) => handleRemove(removeDialog.link_id, reason)}
        />
      )}
      <SelectShipmentsForConsolidationDialog
        open={selectShipmentsOpen}
        onOpenChange={setSelectShipmentsOpen}
        consolidation={data}
        alreadyLinkedIds={(data.shipments || []).map((s) => s.shipping_label_id).filter(Boolean)}
        onAdded={() => load()}
      />
      {shipmentMatch && <ConsolidationShipmentChoice match={shipmentMatch} busy={addBusy} onClose={() => setShipmentMatch(null)} onChoose={id => handleAddShipments(shipmentMatch.wNumbers, shipmentMatch.force, { ...shipmentMatch.choices, [shipmentMatch.detail.w_number]: id })} />}
      {lookupOpen && (
        <ShipmentLookupDialog
          onClose={() => setLookupOpen(false)}
          onOpenShipment={(ship) => {
            if (ship?.id) {
              window.open(
                `/admin?section=warehouse&shipment_id=${encodeURIComponent(ship.id)}`,
                "_blank", "noopener"
              );
            }
          }}
        />
      )}
    </div>
  );
}

// =====================  Shipments tab  =====================

function formatZonedTimestamp(value, timeZone) {
  if (!value || !Number.isFinite(new Date(value).getTime())) return "—";
  try {
    return new Intl.DateTimeFormat("nl-NL", { timeZone, year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23", timeZoneName: "short" }).format(new Date(value));
  } catch {
    return "Onbekende tijdzone";
  }
}

function ShipmentsTab({ data, statusLabels, canAdd, canEdit, isLocked, addBusy, onAdd, onRemove, onShipmentPatched }) {
  const shipments = data.shipments || [];
  const t = data.totals || {};
  const { hasPermission } = useAuth();

  // Fase 3 (Feb 2026) — metadata voor inline dropdowns. Cache single fetch
  // van shipping-labels meta zodat we niet per rij opnieuw laden.
  const [labelMeta, setLabelMeta] = useState(null);
  useEffect(() => {
    if (!canEdit) return;
    let cancelled = false;
    adminApi.get("/admin/shipping-labels/meta")
      .then((r) => { if (!cancelled) setLabelMeta(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canEdit]);

  const canEditPayment = hasPermission("invoices:mark_paid");

  const handleInlineSave = async (sid, field, value) => {
    try {
      const r = await adminApi.patch(
        `/admin/shipping-labels/${sid}`, { [field]: value },
      );
      // Backend retourneert het bijgewerkte label; we mergen alleen het
      // gewijzigde veld in de detail-state om onnodige re-renders te
      // voorkomen. Behoud eventuele derived-velden zoals label_status.
      if (onShipmentPatched) {
        onShipmentPatched(sid, { [field]: r.data?.[field] ?? value });
      }
      toast.success("Bijgewerkt");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bijwerken mislukt");
      return false;
    }
  };

  return (
    <div className="space-y-4">
      {canAdd && <WChipInput busy={addBusy} onAdd={onAdd} />}

      {shipments.length === 0 ? (
        <div className="text-center py-10 text-gray-500" data-testid="shipments-empty">
          <Boxes className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">Nog geen zendingen gekoppeld.</p>
          {canAdd && <p className="text-xs mt-1">Voer W-nummer(s) in hierboven om te beginnen.</p>}
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200">
          <table className="w-full text-xs min-w-[1100px]">
            <thead className="bg-gray-50 text-[10px] uppercase font-bold text-gray-600">
              <tr>
                <th className="text-left p-2" data-testid="shipments-number-status-heading">W-nummer / status</th>
                <th className="text-left p-2">Label Type</th>
                <th className="text-left p-2">Diensttype</th>
                <th className="text-left p-2">Bestemming</th>
                <th className="text-left p-2">Ontvanger</th>
                <th className="text-right p-2">Colli</th>
                <th className="text-right p-2">Gewicht</th>
                <th className="text-right p-2">CBM</th>
                <th className="text-right p-2">Waarde</th>
                <th className="text-center p-2">Betaalstatus</th>
                {canEdit && <th className="text-right p-2">Acties</th>}
              </tr>
            </thead>
            <tbody>
              {shipments.map((s) => {
                const sid = s.shipping_label_id || s.id || s.label_id;
                const rowEditable = canEdit && !isLocked && !!sid;
                return (
                <tr key={s.link_id} className="border-t border-gray-100 hover:bg-gray-50"
                    data-testid={`shipment-row-${s.link_id}`}>
                  <td className="p-2 font-mono font-bold text-[#082c50]">
                    <span data-testid={`shipment-row-number-${s.link_id}`}>{s.w_number}</span>
                    <div className="mt-1 max-w-[15rem] whitespace-normal break-words font-sans text-[11px] font-medium text-blue-700"
                      data-testid={`shipment-row-operational-status-${s.link_id}`}>
                      {statusLabels[s.operational_status] || s.operational_status || "Nog geen milestone"}
                    </div>
                  </td>
                  <td className="p-2">
                    <InlineDropdown
                      value={s.label_type || "package"}
                      options={LABEL_TYPE_OPTIONS}
                      editable={rowEditable}
                      onSave={(v) => handleInlineSave(sid, "label_type", v)}
                      testid={`shipment-row-label-type-${s.link_id}`}
                    />
                  </td>
                  <td className="p-2">
                    <InlineDropdown
                      value={s.service_type || "package"}
                      options={labelMeta?.service_types || []}
                      editable={rowEditable}
                      onSave={(v) => handleInlineSave(sid, "service_type", v)}
                      testid={`shipment-row-service-${s.link_id}`}
                      renderBadge={(val) => (
                        <ServiceTypeBadge serviceType={val} testId={`shipment-row-service-badge-${s.link_id}`} />
                      )}
                    />
                  </td>
                  <td className="p-2">
                    <DestinationBadge code={s.destination_country_code} name={s.destination_country_name}
                                      testId={`shipment-row-destination-${s.link_id}`} />
                  </td>
                  <td className="p-2">
                    {s.recipient_name}
                    {s.recipient_city && <div className="text-[10px] text-gray-500">{s.recipient_city}</div>}
                  </td>
                  <td className="p-2 text-right font-mono">{s.boxes}</td>
                  <td className="p-2 text-right font-mono">{fmtNum(s.weight_kg, 1)} kg</td>
                  <td className="p-2 text-right font-mono">{fmtNum(s.volume_cbm, 4)}</td>
                  <td className="p-2 text-right font-mono">
                    {s.declared_value > 0 ? `${s.declared_value_currency || "EUR"} ${fmtNum(s.declared_value)}` : "—"}
                  </td>
                  <td className="p-2 text-center">
                    <InlineDropdown
                      value={s.payment_status || "not_paid"}
                      options={labelMeta?.payment_statuses || []}
                      editable={rowEditable && canEditPayment}
                      onSave={(v) => handleInlineSave(sid, "payment_status", v)}
                      testid={`shipment-row-payment-status-${s.link_id}`}
                      readOnlyReason={
                        rowEditable && !canEditPayment
                          ? "Alleen rollen met financiële permissie mogen dit"
                          : null
                      }
                    />
                  </td>
                  {canEdit && (
                    <td className="p-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => onRemove(s)}
                              className="h-7 px-2 text-red-600 hover:bg-red-50"
                              data-testid={`shipment-remove-${s.link_id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 font-bold text-xs border-t-2 border-gray-200">
              <tr>
                <td className="p-2">Totaal</td>
                <td colSpan={3}></td>
                <td className="p-2 text-right font-mono" data-testid="totals-boxes">{t.boxes || 0}</td>
                <td className="p-2 text-right font-mono" data-testid="totals-weight">{fmtNum(t.weight_kg, 1)} kg</td>
                <td className="p-2 text-right font-mono" data-testid="totals-cbm">{fmtNum(t.volume_cbm, 4)}</td>
                <td></td>
                <td className="p-2 text-right font-mono" data-testid="totals-declared">
                  {t.declared_value ? `${t.declared_value_currency || "EUR"} ${fmtNum(t.declared_value)}` : "—"}
                </td>
                <td colSpan={canEdit ? 3 : 2}></td>
              </tr>
              <tr>
                <td className="p-2 text-gray-500 font-normal" colSpan={canEdit ? 12 : 11}>
                  Totaal aantal zendingen: <strong data-testid="totals-count">{t.count || 0}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

// Fase 3 (Feb 2026) — vaste opties voor Label Type (backend accepteert
// alleen deze twee waarden voor label_type). Als er nieuwe types komen,
// hier ook bijwerken.
const LABEL_TYPE_OPTIONS = [
  { key: "package",           label: "Package (standaard)" },
  { key: "warehouse_package", label: "Warehouse Package" },
];

// =====================  W-number chip input  =====================

function WChipInput({ busy, onAdd }) {
  const [text, setText] = useState("");
  const [chips, setChips] = useState([]);

  const commitChips = (raw) => {
    const pieces = String(raw).split(/[,\s;\r\n]+/).map((p) => p.trim().toUpperCase()).filter(Boolean);
    if (pieces.length === 0) return;
    setChips((s) => Array.from(new Set([...s, ...pieces])));
    setText("");
  };

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (text.trim()) {
        commitChips(text);
      } else if (chips.length > 0) {
        submit();
      }
    } else if (e.key === "Backspace" && !text && chips.length > 0) {
      setChips((s) => s.slice(0, -1));
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text");
    if (/[,\s;\n]/.test(pasted)) {
      e.preventDefault();
      commitChips(pasted);
    }
  };

  const remove = (idx) => setChips((s) => s.filter((_, i) => i !== idx));

  const submit = async () => {
    const all = text.trim() ? [...chips, ...text.split(/[,\s;\r\n]+/).map((p) => p.trim().toUpperCase()).filter(Boolean)] : chips;
    if (all.length === 0) return;
    await onAdd(all);
    setChips([]);
    setText("");
  };

  return (
    <div className="bg-blue-50 border border-blue-100 p-3" data-testid="w-chip-input-wrapper">
      <Label className="text-[10px] uppercase font-bold tracking-wide text-[#082c50] mb-1.5 block">
        Zendingen toevoegen
      </Label>
      <div className="flex items-center flex-wrap gap-1.5 bg-white border border-gray-300 px-2 py-1.5 min-h-[42px]">
        {chips.map((c, i) => (
          <span key={i} className="inline-flex items-center bg-[#082c50] text-white text-xs font-mono px-2 py-0.5 rounded-sm"
                data-testid={`w-chip-${c}`}>
            {c}
            <button onClick={() => remove(i)} className="ml-1 hover:text-red-200" aria-label={`Verwijder ${c}`}>
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKey}
          onPaste={handlePaste}
          placeholder={chips.length === 0 ? "Voer W-nummer(s) in en druk op Enter" : ""}
          className="flex-1 min-w-[200px] outline-none border-0 text-xs font-mono px-1"
          data-testid="w-chip-input"
          autoComplete="off"
        />
        <Button size="sm" onClick={submit}
                disabled={busy || (chips.length === 0 && !text.trim())}
                className="bg-[#0055FF] hover:bg-[#0044CC] text-white rounded-none text-xs font-bold uppercase h-7"
                data-testid="w-chip-submit">
          {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
          Toevoegen
        </Button>
        <ScanButton
          iconOnly
          testId="w-chip-scan"
          title="Scan W-nummer / barcode en voeg toe"
          className="h-7 px-2 ml-1"
          onResult={(v) => {
            const clean = (v || "").trim().toUpperCase();
            if (clean) setChips((c) => Array.from(new Set([...c, clean])));
          }}
        />
      </div>
      <p className="text-[10px] text-gray-500 mt-1.5">
        Tip: scan, plak meerdere W-nummers tegelijk (regel- of komma-gescheiden). Druk op <kbd className="px-1 border border-gray-300 bg-white text-[9px]">Enter</kbd> per chip of voor de hele lijst.
      </p>
    </div>
  );
}

// =====================  Activity tab  =====================

function ActivityTab({ consolidationId }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await adminApi.get("/admin/audit-logs", { params: { limit: 200 } });
        const filtered = (r.data || []).filter((row) => row.target_id === consolidationId);
        setLogs(filtered);
      } catch {
        // Audit-permissie niet voor iedereen — toon nette fallback.
        setLogs([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [consolidationId]);
  if (loading) return <div className="text-center py-6 text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Laden…</div>;
  if (logs.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500" data-testid="activity-empty">
        <Activity className="w-8 h-8 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">Geen activiteit (of geen rechten op auditlog).</p>
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="activity-list">
      {logs.map((l) => (
        <li key={l.id} className="border-l-2 border-[#0055FF] pl-3 py-1 text-xs">
          <div className="font-mono text-gray-500">{(l.created_at || "").slice(0, 19).replace("T", " ")}</div>
          <div className="font-bold text-[#082c50]">{l.action.replace(/_/g, " ")}</div>
          <div className="text-gray-600">{l.actor_email || "system"}</div>
          {l.new_value && (
            <pre className="text-[10px] text-gray-500 mt-0.5 whitespace-pre-wrap font-mono">{JSON.stringify(l.new_value, null, 2)}</pre>
          )}
        </li>
      ))}
    </ul>
  );
}

// =====================  Helpers / dialogs  =====================

/* ─── Inklapbare detailsectie ────────────────────────────────
   Standaard DICHT zodat de consolidatiesamenvatting bovenaan dominant blijft.
   Alle bestaande velden en edit/save-logica blijven onaangeroerd; ze worden
   alleen pas gemount wanneer de sectie open staat. */
function CollapsibleSection({ icon, title, hint, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-gray-200" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center gap-1.5 px-3 py-2.5 text-[#082c50] hover:bg-slate-50 transition-colors text-left"
        data-testid={`${testid}-toggle`}
      >
        {icon}
        <h3 className="text-[10px] font-chivo font-black uppercase tracking-wider">
          {title}
        </h3>
        <span className="ml-auto flex items-center gap-2">
          {hint && (
            <span className="text-[9px] uppercase font-bold text-gray-400 tracking-wider hidden md:inline">
              {hint}
            </span>
          )}
          <ChevronDown
            className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3" data-testid={`${testid}-body`}>
          {children}
        </div>
      )}
    </div>
  );
}

function InfoCard({ icon, title, children, testid }) {
  return (
    <div className="bg-white border border-gray-200 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2 text-[#082c50]">
        {icon}
        <h3 className="text-[10px] font-chivo font-black uppercase tracking-wider">{title}</h3>
      </div>
      <div className="space-y-1 text-xs">{children}</div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-500 text-[10px] uppercase font-bold">{k}</span>
      <span className="text-gray-900 text-right text-xs">{v}</span>
    </div>
  );
}

/**
 * EditableField — inline-editable field met expliciete Save/Cancel.
 *
 * Fase 2 (Feb 2026) helper voor Container / ETD / ETA op de consolidation
 * detail-pagina. Klik op de waarde (of het "Bewerken" icoon) om in edit-mode
 * te gaan; Escape annuleert, Enter (of "Opslaan") slaat op via de meegegeven
 * `onSave(value)` callback. Als `editable` false is toont het component
 * alleen de waarde (read-only) — zo respecteren we RBAC + status-locks.
 */
function EditableField({
  label, value, onSave, placeholder = "", type = "text",
  testid, editable = true,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value || ""); }, [value]);

  const startEdit = () => {
    if (!editable || saving) return;
    setDraft(value || "");
    setEditing(true);
  };
  const cancel = () => {
    setDraft(value || "");
    setEditing(false);
  };
  const save = async () => {
    if (saving) return;
    const trimmed = (draft || "").trim();
    if (trimmed === (value || "").trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    const ok = await onSave(trimmed);
    setSaving(false);
    if (ok) setEditing(false);
  };
  const onKey = (e) => {
    if (e.key === "Escape") { e.preventDefault(); cancel(); }
    if (e.key === "Enter" && type !== "textarea") { e.preventDefault(); save(); }
  };

  const displayValue = value ? (
    <span className="font-mono text-sm text-gray-900">{value}</span>
  ) : (
    <span className="italic text-sm text-gray-400">Niet ingesteld</span>
  );

  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="text-[9px] uppercase font-bold text-gray-500 tracking-wider">
        {label}
      </div>
      {editing ? (
        <div className="flex items-center gap-1">
          <Input
            type={type}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            placeholder={placeholder}
            className="h-8 rounded-none text-sm font-mono flex-1"
            disabled={saving}
            autoFocus
            data-testid={`${testid}-input`}
          />
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="h-8 rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold uppercase px-2"
            data-testid={`${testid}-save`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Opslaan"}
          </Button>
          <Button
            size="sm"
            onClick={cancel}
            disabled={saving}
            variant="outline"
            className="h-8 rounded-none text-[10px] uppercase px-2"
            data-testid={`${testid}-cancel`}
          >
            <X className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <button
          type="button"
          onClick={startEdit}
          disabled={!editable}
          className={`w-full text-left border border-gray-200 px-3 py-2 min-h-[32px] flex items-center justify-between gap-2 ${
            editable
              ? "hover:border-[#0055FF] hover:bg-blue-50/30 cursor-pointer"
              : "cursor-not-allowed opacity-70"
          }`}
          data-testid={`${testid}-value`}
        >
          {displayValue}
          {editable && (
            <span className="text-[9px] uppercase text-gray-400 font-bold">
              Bewerken
            </span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * EditableSelectField — click-to-edit dropdown voor canonical enum-velden
 * (consolidatie `type` en `measurement`). Zelfde interactiepatroon als
 * EditableField zodat de detailpagina één consistente edit-UX houdt.
 */
function EditableSelectField({
  label, value, options, onSave, testid, editable = true,
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const current = options.find((o) => o.value === value);

  const handleChange = async (next) => {
    if (next === value) { setEditing(false); return; }
    setSaving(true);
    const ok = await onSave(next);
    setSaving(false);
    if (ok) setEditing(false);
  };

  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="text-[9px] uppercase font-bold text-gray-500 tracking-wider">
        {label}
      </div>
      {editing ? (
        <Select value={value || ""} onValueChange={handleChange} disabled={saving}>
          <SelectTrigger className="h-8 rounded-none text-sm"
                         data-testid={`${testid}-select`}>
            <SelectValue placeholder="Kies…" />
          </SelectTrigger>
          <SelectContent>
            {options.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <button
          type="button"
          onClick={() => editable && !saving && setEditing(true)}
          disabled={!editable}
          className={`w-full text-left border border-gray-200 px-3 py-2 min-h-[32px] flex items-center justify-between gap-2 ${
            editable
              ? "hover:border-[#0055FF] hover:bg-blue-50/30 cursor-pointer"
              : "cursor-not-allowed opacity-70"
          }`}
          data-testid={`${testid}-value`}
        >
          {current
            ? <span className="text-sm text-gray-900">{current.label}</span>
            : <span className="italic text-sm text-gray-400">Niet ingesteld</span>}
          {editable && (
            <span className="text-[9px] uppercase text-gray-400 font-bold">Bewerken</span>
          )}
        </button>
      )}
    </div>
  );
}

/**
 * InlineDropdown — click-to-edit dropdown voor tabelcellen.
 *
 * Fase 3 (Feb 2026) helper voor inline shipment-edit (label_type,
 * service_type, payment_status) in de ConsolidationDetail zendingen-tab.
 *
 * Gedrag:
 *   - Read-only mode: toont de waarde als badge (via renderBadge) of tekst.
 *   - Editable mode: klik → dropdown → change → auto-save via onSave.
 *   - onSave moet true (persisted) of false (failed) retourneren; bij false
 *     rolt de select terug naar de vorige waarde.
 *   - `readOnlyReason` toont een tooltip als de gebruiker technisch wel de
 *     dropdown kan zien maar mist een specifieke permissie (bv. payment
 *     status voor niet-financiële rollen).
 */
function InlineDropdown({
  value, options, onSave, editable = true, testid,
  renderBadge, readOnlyReason,
}) {
  const [current, setCurrent] = useState(value);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setCurrent(value); }, [value]);

  const handleChange = async (newValue) => {
    if (newValue === current) return;
    const previous = current;
    setCurrent(newValue);
    setSaving(true);
    const ok = await onSave(newValue);
    setSaving(false);
    if (!ok) setCurrent(previous);
  };

  const displayFor = (val) => {
    if (renderBadge) return renderBadge(val);
    const opt = (options || []).find((o) => o.key === val || o.value === val);
    return (
      <span className="text-[10px] uppercase font-bold text-gray-700">
        {opt?.label || val || "—"}
      </span>
    );
  };

  if (!editable) {
    return (
      <div
        className="inline-flex items-center gap-1"
        data-testid={testid}
        title={readOnlyReason || undefined}
      >
        {displayFor(current)}
        {readOnlyReason && (
          <span className="text-[8px] uppercase text-gray-400 font-bold">🔒</span>
        )}
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1" data-testid={testid}>
      <Select value={current} onValueChange={handleChange} disabled={saving}>
        <SelectTrigger
          className="h-7 px-2 rounded-none text-[11px] font-mono min-w-[110px] max-w-[200px]"
          data-testid={`${testid}-trigger`}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent>
          {(options || []).map((o) => (
            <SelectItem
              key={o.key || o.value}
              value={o.key || o.value}
              data-testid={`${testid}-option-${o.key || o.value}`}
            >
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {saving && <Loader2 className="w-3 h-3 animate-spin text-gray-400" />}
    </div>
  );
}

function StubButton({ label, icon, testid }) {
  return (
    <Button size="sm" variant="outline"
            onClick={() => toast.info(`${label}: komt binnenkort`)}
            className="rounded-none text-xs text-gray-500" data-testid={testid}>
      {icon} <span className="ml-1">{label}</span>
      <span className="ml-1 text-[9px] uppercase">soon</span>
    </Button>
  );
}

/**
 * ExportMenu — P0.18.1 Consolidation Export Center (Aug 2026).
 *
 * 6 formaten via één dropdown. Elke item triggert een download door de
 * PDF/XLSX/CSV/JSON/XML bytes via de backend endpoint op te halen en met
 * een blob URL in een nieuw tabblad naar de browser te sturen. Loading-
 * indicator per keuze; toast bevestigt succes/fout.
 */
function ExportMenu({ consolidationId, consolidationNumber }) {
  const [busy, setBusy] = useState(null);
  const { runTask } = useGlobalLoading();

  const downloadExport = async (formatKey, endpoint, extension) => {
    if (!consolidationId) return;
    const isManifestPdf = formatKey.startsWith("manifest");
    setBusy(formatKey);
    try {
      if (isManifestPdf) {
        // P0.20 SPRINT — professionele enterprise-grade manifest generatie
        // via de centrale GlobalLoadingProvider. Toont een fullscreen
        // overlay met gestapelde progress messages en dedupliceert
        // dubbele klikken via de unique task ID.
        await runTask({
          id: `export-${formatKey}-${consolidationId}`,
          message: "Manifest wordt gegenereerd…",
          subMessage: "De consolidatie wordt gecontroleerd en professioneel opgemaakt. Even geduld…",
          successMessage: `Manifest ${consolidationNumber || ""} gedownload`,
          run: async (update) => {
            update("Manifest wordt gegenereerd…", "Data verzamelen…");
            await new Promise((r) => setTimeout(r, 120));
            update("Manifest wordt gegenereerd…", "Klanten laden…");
            await new Promise((r) => setTimeout(r, 120));
            update("Manifest wordt gegenereerd…", "Manifest opmaken (portrait A4)…");
            const resp = await adminApi.get(
              `/admin/consolidations/${consolidationId}${endpoint}`,
              { responseType: "blob" },
            );
            update("Manifest wordt gegenereerd…", "PDF genereren…");
            const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
            const url = window.URL.createObjectURL(blob);
            let filename = `${consolidationNumber || consolidationId}-manifest${extension}`;
            const disp = resp.headers["content-disposition"] || "";
            const match = disp.match(/filename="?([^";]+)"?/);
            if (match) filename = match[1];
            update("Manifest wordt gegenereerd…", "Download voorbereiden…");
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
          },
        });
      } else {
        const resp = await adminApi.get(
          `/admin/consolidations/${consolidationId}${endpoint}`,
          { responseType: "blob" },
        );
        const blob = new Blob([resp.data], { type: resp.headers["content-type"] });
        const url = window.URL.createObjectURL(blob);
        let filename = `${consolidationNumber || consolidationId}-manifest${extension}`;
        const disp = resp.headers["content-disposition"] || "";
        const match = disp.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1];
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success(`Export ${formatKey.toUpperCase()} gedownload`);
      }
    } catch (err) {
      if (err?.message === "DUPLICATE_TASK") return;
      // Export gebruikt responseType=blob, dus err.response.data is een Blob.
      // Bij een JSON error-body moeten we hem eerst als text lezen om de
      // .detail te kunnen tonen. Fallback naar err.message als niet leesbaar.
      let detail = err?.message || "Onbekende fout";
      const data = err?.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            if (parsed?.detail) detail = parsed.detail;
          } catch {
            // Geen JSON — toon raw text als het compact genoeg is.
            if (text && text.length < 400) detail = text;
          }
        } catch { /* blob niet leesbaar — behoud fallback */ }
      } else if (typeof data === "object" && data?.detail) {
        detail = data.detail;
      } else if (typeof data === "string" && data.length < 400) {
        detail = data;
      }
      toast.error(`Export mislukt: ${detail}`);
    } finally {
      setBusy(null);
    }
  };

  const items = [
    {
      key: "manifest-sea",
      label: "Sea Manifest (PDF)",
      icon: <Ship className="w-3 h-3 mr-2 text-[#082c50]" />,
      endpoint: "/export/manifest-sea.pdf",
      ext: ".pdf",
    },
    {
      key: "manifest-air",
      label: "Air Manifest (PDF)",
      icon: <Plane className="w-3 h-3 mr-2 text-[#082c50]" />,
      endpoint: "/export/manifest-air.pdf",
      ext: ".pdf",
    },
    {
      key: "xlsx",
      label: "Excel spreadsheet (XLSX)",
      icon: <FileSpreadsheet className="w-3 h-3 mr-2 text-emerald-700" />,
      endpoint: "/export.xlsx",
      ext: ".xlsx",
    },
    {
      key: "csv",
      label: "CSV (semikolon)",
      icon: <FileText className="w-3 h-3 mr-2 text-gray-600" />,
      endpoint: "/export.csv",
      ext: ".csv",
    },
    {
      key: "json",
      label: "JSON",
      icon: <FileCode className="w-3 h-3 mr-2 text-orange-600" />,
      endpoint: "/export.json",
      ext: ".json",
    },
    {
      key: "xml",
      label: "XML",
      icon: <FileCode className="w-3 h-3 mr-2 text-purple-600" />,
      endpoint: "/export.xml",
      ext: ".xml",
    },
  ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className="rounded-none text-xs"
          data-testid="consolidation-action-export"
          disabled={busy !== null || !consolidationId}
        >
          {busy
            ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            : <Download className="w-3 h-3 mr-1" />}
          Exporteren <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64 max-w-[calc(100vw-2rem)]">
        {items.map((it) => (
          <DropdownMenuItem
            key={it.key}
            onClick={() => downloadExport(it.key, it.endpoint, it.ext)}
            disabled={busy !== null}
            data-testid={`consolidation-export-${it.key}`}
          >
            {busy === it.key
              ? <Loader2 className="w-3 h-3 mr-2 animate-spin" />
              : it.icon}
            <span className="text-xs">{it.label}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function StubTab({ title, hint, testid }) {
  return (
    <div className="text-center py-10" data-testid={testid}>
      <FileText className="w-10 h-10 mx-auto text-gray-300 mb-2" />
      <h3 className="font-chivo font-black text-base text-[#082c50]">{title}</h3>
      <p className="text-xs text-gray-500 mt-1">{hint}</p>
      <span className="inline-block mt-2 text-[9px] uppercase tracking-wider font-bold text-gray-400 bg-gray-100 px-2 py-0.5">
        Komt binnenkort
      </span>
    </div>
  );
}

function StatusChangeDialog({ current, options, labels, onClose, onConfirm }) {
  const [requestId] = useState(() => crypto.randomUUID());
  const [status, setStatus] = useState(current);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="status-change-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-[#082c50]">Status wijzigen</DialogTitle>
          <DialogDescription>Verander de status van deze consolidatie.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-[10px] uppercase font-bold">Nieuwe status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="rounded-none mt-1" data-testid="status-change-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {options.map((s) => <SelectItem key={s} value={s} data-testid={`status-change-option-${s.replace(":", "-")}`}>
                  {labels[s] || s}
                </SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Reden (optioneel)</Label>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      className="rounded-none mt-1 text-xs"
                      data-testid="status-change-reason" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none text-xs" data-testid="status-change-cancel">Annuleren</Button>
          <Button onClick={async () => { setBusy(true); await onConfirm(status, reason.trim() || null, requestId); setBusy(false); }}
                  disabled={busy || !status || !options.includes(status)}
                  className="bg-[#0055FF] text-white rounded-none text-xs font-bold uppercase"
                  data-testid="status-change-confirm">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            Wijzigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RemoveShipmentDialog({ shipment, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="remove-shipment-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Zending verwijderen
          </DialogTitle>
          <DialogDescription>
            <span className="font-mono font-bold">{shipment.w_number}</span> wordt uit deze consolidatie verwijderd.
            De zending zelf blijft bestaan; je verwijdert alleen de koppeling.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-[10px] uppercase font-bold">Reden (verplicht) *</Label>
          <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                    placeholder="Bijv. verkeerd ingescand, klant heeft annuleerd…"
                    className="rounded-none mt-1 text-xs"
                    data-testid="remove-shipment-reason" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-none text-xs">Terug</Button>
          <Button onClick={async () => {
            if (!reason.trim()) { toast.error("Reden is verplicht"); return; }
            setBusy(true); await onConfirm(reason.trim()); setBusy(false);
          }} disabled={busy}
                  className="bg-red-600 hover:bg-red-700 text-white rounded-none text-xs font-bold uppercase"
                  data-testid="remove-shipment-confirm">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
            Ja, verwijderen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
